"use client";

import React, { useEffect, useRef, useState } from "react";
import { Eye, Calculator, ClipboardCheck, House, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const steps = [{
  id: 1,
  title: "Instant Quote",
  description: "Get a precise, transparent cost estimate in minutes using our advanced digital inventory tool.",
  icon: Calculator
}, {
  id: 2,
  title: "Logistics Plan",
  description: "Our experts map out every detail, from parking permits to specialized packing for fragile items.",
  icon: Eye
}, {
  id: 3,
  title: "The Move",
  description: "Professional crews execute the shift with kinetic precision, ensuring your assets are safe and secure.",
  icon: ClipboardCheck
}, {
  id: 4,
  title: "Unpack & Settle",
  description: "We don't just drop boxes; we help you settle into your new space so you can start living immediately.",
  icon: House
}];
export default function TheMovingBlueprint() {
  const [progress, setProgress] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const start = windowHeight * 0.75;
      const end = -rect.height + windowHeight * 0.25;
      const current = rect.top;
      if (current > start) {
        setProgress(0);
      } else if (current < end) {
        setProgress(100);
      } else {
        const totalDistance = start - end;
        const scrolledDistance = start - current;
        setProgress(scrolledDistance / totalDistance * 100);
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="264992" id="the-moving-blueprint" ref={sectionRef} className="py-24 bg-background text-foreground relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-secondary mb-6 tracking-tight uppercase">
            The Moving Blueprint
          </h2>
          <p className="text-lg font-default text-muted-foreground">
            Experience Kinetic Precision. Our streamlined 4-step methodology eliminates the stress of relocation through industrial-strength planning and digital sophistication.
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          {}
          <div className="absolute top-[48px] left-[12%] right-[12%] h-1.5 z-0 hidden md:block">
            <Progress value={progress} className="h-full bg-muted [&>div]:bg-primary" />
          </div>

          <Carousel opts={{
          align: "start",
          dragFree: true
        }} className="w-full">
            <CarouselContent className="pb-8 pt-4">
              {steps.map((step, i) => <CarouselItem key={step.id} data-index={i} className="md:basis-1/2 lg:basis-1/4">
                  <div className="flex flex-col items-center text-center relative z-10 px-3 h-full">
                    {}
                    <div className="w-16 h-16 rounded-lg bg-background border-4 border-background shadow-md flex items-center justify-center mb-6 relative shrink-0 transition-transform duration-300 hover:scale-110">
                      <div className="w-full h-full rounded-md bg-primary flex items-center justify-center text-primary-foreground">
                        <step.icon className="w-7 h-7" />
                      </div>
                    </div>

                    {}
                    <Card className="w-full flex-1 transition-all duration-300 hover:-translate-y-2 hover:shadow-lg border-border bg-card flex flex-col">
                      <CardHeader className="pb-4">
                        <CardTitle className="text-xl font-default font-bold text-secondary">
                          {i + 1}. {step.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="flex-1">
                        <p className="text-muted-foreground text-sm leading-relaxed font-default">
                          {step.description}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>

            {}
            <div className="flex items-center justify-center gap-4 mt-4 lg:hidden">
              <CarouselPrevious className="static translate-y-0 translate-x-0 border-primary text-primary hover:bg-primary hover:text-primary-foreground" />
              <CarouselNext className="static translate-y-0 translate-x-0 border-primary text-primary hover:bg-primary hover:text-primary-foreground" />
            </div>
          </Carousel>
        </div>

        <div className="mt-16 text-center">
          <Button asChild size="lg" className="rounded-md shadow-md hover:shadow-xl transition-all bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/GetQuotePage">
              Start Your Shift
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}